// Client-side video preflight: validates a file BEFORE we kick off an upload
// AND captures candidate poster frames so creators can pick one from the
// movie itself instead of designing a separate poster.

const MAX_DURATION_SECONDS = 7200;
const MIN_WIDTH = 240;
const MIN_HEIGHT = 180;
const METADATA_TIMEOUT_MS = 30000;
const POSTER_FRAME_OFFSETS = [0.1, 0.3, 0.5, 0.7, 0.9]; // 5 candidates spread across the video
const CODEC_HELP_URL = "https://developers.cloudflare.com/stream/faq/#what-input-file-formats-are-supported";

export function preflightVideo(file) {
  return new Promise((resolve) => {
    if (file.type && !file.type.startsWith("video/")) {
      return resolve({
        ok: false,
        error: `That's a ${file.type || "non-video"} file. Please select a video (MP4 / MOV / WebM).`,
      });
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";

    let settled = false;
    const cleanup = () => { try { URL.revokeObjectURL(url); } catch { /* noop */ } };
    const fail = (error, helpUrl) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ ok: false, error, helpUrl });
    };
    const ok = (extra) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ ok: true, ...extra });
    };

    const timeout = setTimeout(() => {
      fail(
        "Video metadata took too long to load — the file may be too large or corrupt. Try a smaller / re-exported file.",
        CODEC_HELP_URL,
      );
    }, METADATA_TIMEOUT_MS);

    video.onerror = () => {
      clearTimeout(timeout);
      fail(
        "Couldn't decode this video. Cloudflare accepts MP4 (H.264 video + AAC audio) and WebM (VP8/VP9). Try re-exporting from your editor.",
        CODEC_HELP_URL,
      );
    };

    video.onloadedmetadata = async () => {
      clearTimeout(timeout);
      const { duration, videoWidth, videoHeight } = video;

      if (!duration || !isFinite(duration)) {
        return fail(
          "Video has no valid duration. The container may be corrupt or use a fragmented MP4 — re-export to a standard MP4.",
          CODEC_HELP_URL,
        );
      }
      if (duration > MAX_DURATION_SECONDS) {
        const mins = Math.round(duration / 60);
        const limit = MAX_DURATION_SECONDS / 60;
        return fail(`Video is ${mins} min — current limit is ${limit} min. Trim it or contact the admin to raise the duration cap.`);
      }
      if (videoWidth < MIN_WIDTH || videoHeight < MIN_HEIGHT) {
        return fail(`Resolution ${videoWidth}×${videoHeight} is too small. Minimum for streaming is ${MIN_WIDTH}×${MIN_HEIGHT}.`);
      }

      // Capture multiple frames as poster candidates. Sequential (single <video>
      // element can only be at one currentTime at a time).
      const scale = Math.min(1, 640 / videoWidth);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(videoWidth * scale);
      canvas.height = Math.round(videoHeight * scale);
      const ctx = canvas.getContext("2d");

      const frames = [];
      for (const frac of POSTER_FRAME_OFFSETS) {
        const t = Math.min(Math.max(duration * frac, 0.2), Math.max(duration - 0.2, 0.2));
        try {
          await new Promise((res) => {
            const onSeeked = () => { video.removeEventListener("seeked", onSeeked); res(); };
            video.addEventListener("seeked", onSeeked);
            // safety timeout in case 'seeked' never fires
            const safety = setTimeout(() => { video.removeEventListener("seeked", onSeeked); res(); }, 4000);
            video.currentTime = t;
            // clear timer when resolved (best-effort)
            video.addEventListener("seeked", () => clearTimeout(safety), { once: true });
          });
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          frames.push({ time: t, dataUrl: canvas.toDataURL("image/jpeg", 0.78) });
        } catch {
          // skip this offset; keep going
        }
      }

      ok({
        duration,
        width: videoWidth,
        height: videoHeight,
        sizeBytes: file.size,
        thumbnailDataUrl: frames[0]?.dataUrl || null,
        frameSuggestions: frames,
      });
    };
  });
}

export function formatDuration(seconds) {
  if (!seconds || !isFinite(seconds)) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, "0")}s`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export function dataUrlToFile(dataUrl, filename = "poster-from-frame.jpg") {
  const [header, b64] = dataUrl.split(",");
  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const bin = atob(b64);
  const len = bin.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

export { CODEC_HELP_URL };
